import React, {useEffect, useMemo, useState} from 'react';
import {Box, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent} from '@mui/material';
import {DataRow} from "../types";

interface ProductSelectorProps {
    items: any[];
    selectedProducts: string[];
    setSelectedProducts: (products: string[]) => void;
    compoundFilter: 'all' | 'inCommon' | 'notInCommon';
    setCompoundFilter: (value: 'all' | 'inCommon' | 'notInCommon') => void;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({ items, selectedProducts, setSelectedProducts, compoundFilter, setCompoundFilter }) => {
    const [isInitialRender, setIsInitialRender] = useState(true);

    const products = useMemo(() => {
        const productSet = new Set<string>();
        items.forEach(item => {
            if (item.DATA && item.DATA.length > 0) {
                item.DATA.forEach((d: DataRow) => {
                    const productKey = d.PRODUCT
                        ? `${d.PRODUCT || 'Unknown'} (${d.ANALYSIS_TYPE || 'Unknown'})`
                        : `${item.TITLE || 'Unknown'}`;
                    productSet.add(productKey);
                });
            } else {
                productSet.add(`${item.TITLE || 'Unknown'}`);
            }
        });
        const productList = Array.from(productSet).sort();
        return productList;
    }, [items]);

    // Set default selectedProducts to all products on first render
    useEffect(() => {
        if (isInitialRender && selectedProducts.length === 0 && products.length > 0) {
            setSelectedProducts(products);
            setIsInitialRender(false);
        }
    }, [products, selectedProducts, setSelectedProducts, isInitialRender]);

    const labelText = items.length > 0 && items[0].DATA && items[0].DATA[0] && items[0].DATA[0].PRODUCT ? "Products" : "Batches";

    const handleProductChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value as string[];
        if (value.includes('all')) {
            setSelectedProducts(products.length === selectedProducts.length ? [] : products);
        } else {
            setSelectedProducts(value);
        }
    };

    const handleCompoundFilterChange = (event: SelectChangeEvent<'all' | 'inCommon' | 'notInCommon'>) => {
        setCompoundFilter(event.target.value as 'all' | 'inCommon' | 'notInCommon');
    };

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl sx={{ width: 200, maxWidth: 200 }}>
                <InputLabel>{labelText}</InputLabel>
                <Select
                    multiple
                    value={selectedProducts}
                    onChange={handleProductChange}
                    renderValue={(selected) => selected.join(', ')}
                    label={labelText}
                    size="small"
                >
                    <MenuItem value="all">
                        <Checkbox checked={selectedProducts.length === products.length} />
                        <ListItemText primary="View All" />
                    </MenuItem>
                    {products.map((product) => (
                        <MenuItem key={product} value={product}>
                            <Checkbox checked={selectedProducts.includes(product)} />
                            <ListItemText primary={product} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {selectedProducts.length > 1 && (
                <FormControl sx={{ width: 150, maxWidth: 150 }}>
                    <InputLabel>Compound Filter</InputLabel>
                    <Select
                        value={compoundFilter}
                        onChange={handleCompoundFilterChange}
                        label="Compound Filter"
                        size="small"
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="inCommon">In Common</MenuItem>
                        <MenuItem value="notInCommon">Not in Common</MenuItem>
                    </Select>
                </FormControl>
            )}
        </Box>
    );
};

export default ProductSelector;
